import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HelpCircle, Trash2, CheckCircle2, MessageSquare } from "lucide-react";
import { apiBoss } from "@/lib/api";

export default function BossSupportPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTickets = () => {
    setLoading(true);
    apiBoss.get("/boss/support")
      .then(({ data }) => setTickets(data))
      .catch(() => toast.error("Failed to load support requests"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const handleStatusChange = async (sid, newStatus) => {
    try {
      await apiBoss.patch(`/boss/support/${sid}`, { status: newStatus });
      toast.success(`Support ticket marked ${newStatus}`);
      loadTickets();
    } catch (err) {
      toast.error("Failed to update ticket.");
    }
  };

  const handleDelete = async (sid) => {
    if (!window.confirm("Delete support ticket?")) return;
    try {
      await apiBoss.delete(`/boss/support/${sid}`);
      toast.success("Ticket deleted");
      loadTickets();
    } catch (err) {
      toast.error("Failed to delete ticket.");
    }
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between border-b border-slate-800 pb-6">
        <div>
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary font-semibold">Help Desk</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mt-1">Support Requests</h1>
        </div>

        <div className="bg-slate-900 border border-slate-800 px-4 py-2 rounded-2xl text-xs font-mono text-slate-300">
          Total Tickets: <b>{tickets.length}</b>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-950 text-slate-400 uppercase font-mono border-b border-slate-800">
              <tr>
                <th className="py-4 px-6">Requester Name</th>
                <th className="py-4 px-6">Mobile</th>
                <th className="py-4 px-6">Cafe Name</th>
                <th className="py-4 px-6">Message</th>
                <th className="py-4 px-6">Status</th>
                <th className="py-4 px-6">Submitted Date</th>
                <th className="py-4 px-6 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-800/70">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500 font-mono">Loading support tickets...</td>
                </tr>
              ) : tickets.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500 font-mono">No support requests.</td>
                </tr>
              ) : (
                tickets.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-800/40 transition-colors">
                    <td className="py-4 px-6 font-bold text-white">{t.name}</td>
                    <td className="py-4 px-6 font-mono text-slate-400">{t.mobile}</td>
                    <td className="py-4 px-6">{t.cafe_name || "-"}</td>
                    <td className="py-4 px-6 max-w-xs truncate text-slate-300">{t.message}</td>
                    <td className="py-4 px-6">
                      <select
                        value={t.status}
                        onChange={(e) => handleStatusChange(t.id, e.target.value)}
                        className={`px-3 py-1.5 rounded-full text-xs font-mono font-semibold bg-slate-950 border ${
                          t.status === "New"
                            ? "border-primary text-primary"
                            : t.status === "Resolved"
                            ? "border-emerald-500 text-emerald-400"
                            : "border-slate-700 text-slate-300"
                        }`}
                      >
                        <option value="New">New</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Closed">Closed</option>
                      </select>
                    </td>
                    <td className="py-4 px-6 font-mono text-[11px] text-slate-400">
                      {t.created_at ? t.created_at.slice(0, 10) : "-"}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <button
                        onClick={() => handleDelete(t.id)}
                        className="p-2 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
